<template>
  <v-row>
    <v-col cols="12">
      <v-card>
        <v-card-title>
          <span class="font-weight-regular">Onvif Discovery Versions</span>
          <v-divider class="ml-2 mr-2" vertical></v-divider>
          <v-btn @click="getOnvifDiscoveryVersionList" icon>
            <v-icon dark>mdi-sync</v-icon>
          </v-btn>
          <v-spacer></v-spacer>
          <v-text-field
            v-model="search"
            class="mr-2"
            clearable
            dense
            hide-details
            label="Search onvif discovery version.."
            outlined
            prepend-inner-icon="mdi-magnify"
          ></v-text-field>
          <v-btn @click="newVersion()" outlined color="primary">
            <v-icon left> mdi-plus </v-icon>
            New Version
          </v-btn>
        </v-card-title>

        <v-card-text class="mb-0 pb-0 mx-0 px-0">
          <v-data-table
            :loading="onvifDiscoveryVersionTableLoading"
            :search="search"
            :headers="headers"
            :items="onvifDiscoveryVersionList"
            mobile-breakpoint="0"
            ref="devicesTable"
            sort-desc
            :items-per-page="20"
            :footer-props="{
              'items-per-page-options': [10, 20, 40, -1],
            }"
          >
            <template v-slot:[`item.version`]="{ item }">
              <span v-if="item.version"> {{ item.version }} </span>
              <v-icon v-else>mdi-minus</v-icon>
            </template>

            <template v-slot:[`item.release_date`]="{ item }">
              <span v-if="item.release_date"> {{ item.release_date }} </span>
              <v-icon v-else>mdi-minus</v-icon>
            </template>

            <template v-slot:[`item.user_added`]="{ item }">
              <span v-if="item.user_added"> {{ item.user_added }} </span>
              <v-icon v-else>mdi-minus</v-icon>
            </template>

            <template v-slot:[`item.notification_status`]="{ item }">
              <v-icon
                color="green"
                v-if="item.notification_status === 'Successful'"
              >
                mdi-check-bold
              </v-icon>
              <v-btn
                color="purple"
                dark
                small
                v-else-if="item.notification_status === 'Pending'"
                @click="notifyOnvifDiscovery(item)"
                >Notify</v-btn
              >

              <v-chip
                dark
                color="green"
                v-else-if="item.notification_status === 'Notifying'"
                small
              >
                <v-icon left class="slow-ani" small> mdi-cog </v-icon>
                <span>Notifying...</span>
              </v-chip>
              <v-chip
                small
                v-else
                dark
                :color="notificationChipColor(item.notification_status)"
              >
                {{ item.notification_status }}...
              </v-chip>
            </template>

            <template v-slot:[`item.actions`]="{ item }">
              <div class="d-flex flex-row">
                <v-btn
                  v-for="(action, idx) in actionButtons"
                  :key="idx"
                  icon
                  @click="action.func(item)"
                >
                  <v-icon :color="action.color || 'black'">{{
                    action.icon
                  }}</v-icon>
                </v-btn>
              </div>
            </template>
          </v-data-table>
        </v-card-text>

        <new-onvif-discovery-version
          v-if="showNewOnvifDiscoveryVersionDialog"
          :showNewOnvifDiscoveryVersionDialog="
            showNewOnvifDiscoveryVersionDialog
          "
          @closeNewOnvifDiscoveryVersionDialog="
            closeNewOnvifDiscoveryVersionDialog
          "
          @fetchOnvifDiscoveryVersion="fetchOnvifDiscoveryVersion"
        ></new-onvif-discovery-version>

        <edit-onvif-discovery-version
          v-if="showEditOnvifDiscoveryVersionDialog"
          :showEditOnvifDiscoveryVersionDialog="
            showEditOnvifDiscoveryVersionDialog
          "
          :onvifDiscoveryVersion="selectedOnvifDiscoveryVersion"
          @closeEditOnvifDiscoveryVersionDialog="
            showEditOnvifDiscoveryVersionDialog = false
          "
          @fetchOnvifDiscoveryVersion="fetchOnvifDiscoveryVersion"
        ></edit-onvif-discovery-version>
      </v-card>
    </v-col>
  </v-row>
</template>

<script>
import converter from '@/mixins/converter'
import newOnvifDiscoveryVersion from './NewOnvifDiscoveryVersion.vue'
import EditOnvifDiscoveryVersion from './EditOnvifDiscoveryVersion.vue'
import { mapGetters } from 'vuex'
import get from 'lodash/get'
import Swal from 'sweetalert2'
import { apiService } from '../../../src/service/api.service'

export default converter.extend({
  computed: {
    ...mapGetters([
      'onvifDiscoveryVersionList',
      'onvifDiscoveryVersionTableLoading',
      'nonNotifiedOnvifDiscoveryVersionIds'
    ]),
    notificationChipColor: function () {
      return function (state) {
        switch (state) {
          case 'Failed':
            return 'red'
          default:
            return 'orange'
        }
      }
    }
  },
  components: { newOnvifDiscoveryVersion, EditOnvifDiscoveryVersion },
  name: 'onvifDiscoveryVersion',
  data () {
    return {
      showNewOnvifDiscoveryVersionDialog: false,
      showEditOnvifDiscoveryVersionDialog: false,
      selectedOnvifDiscoveryVersion: {},
      search: '',
      headers: [
        { text: 'ID', value: 'id' },
        { text: 'Version', value: 'version' },
        {
          text: 'Release Date',
          value: 'release_date',
          align: 'center'
        },
        { text: 'User Added', value: 'user_added', align: 'center' },
        {
          text: 'Notified Onvif Discovery',
          value: 'notification_status',
          align: 'center'
        },
        { text: 'Actions', value: 'actions', align: 'center' }
      ],
      actionButtons: [
        {
          icon: 'mdi-circle-edit-outline',
          color: 'grey',
          func: this.editOnvifDiscoveryVersion
        },
        {
          icon: 'mdi-delete-outline',
          color: 'red',
          func: this.deleteOnvifDiscoveryVersion
        }
      ]
    }
  },
  mixins: [converter],
  mounted () {
    this.getOnvifDiscoveryVersionList()
  },
  methods: {
    async notifyOnvifDiscovery (item) {
      Swal.fire({
        title: 'Are you sure?',
        text: 'All Client will be notified about this onvif discovery version.',
        icon: 'question',
        showCancelButton: true
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            const onvifDiscoveryVersionIds = [item.id]
            const notificationConfig = await this.$store.dispatch(
              'notifyClients',
              {
                notifyOnvifDiscoveryVersion: true,
                nonNotifiedOnvifDiscoveryVersionIds: onvifDiscoveryVersionIds
              }
            )
            notificationConfig.status = 'Notifying'
            await this.$store.dispatch('updateNotificationStatus', {
              body: notificationConfig
            })
            if (
              this.nonNotifiedOnvifDiscoveryVersionIds.length > 0 &&
              this.nonNotifiedOnvifDiscoveryVersionIds.includes(item.id)
            ) {
              this.$store.dispatch(
                'removeNonNotifiedOnvifDiscoveryVersion',
                item.id
              )
            }
            this.getOnvifDiscoveryVersionList()
            Swal.fire('Success!', 'Started notifying process.', 'success')
          } catch (error) {
            Swal.fire(
              'Warning!',
              'Notifying process cannot be started.',
              'warning'
            )
          }
        }
      })
    },
    async getOnvifDiscoveryVersionList () {
      this.$store.dispatch('getOnvifDiscoveryVersion')
    },

    newVersion () {
      this.showNewOnvifDiscoveryVersionDialog = true
    },
    closeNewOnvifDiscoveryVersionDialog () {
      this.showNewOnvifDiscoveryVersionDialog = false
    },
    fetchOnvifDiscoveryVersion () {
      this.getOnvifDiscoveryVersionList()
      this.showNewOnvifDiscoveryVersionDialog = false
    },
    editOnvifDiscoveryVersion (item) {
      this.selectedOnvifDiscoveryVersion = item
      this.showEditOnvifDiscoveryVersionDialog = true
    },
    deleteOnvifDiscoveryVersion (item) {
      Swal.fire({
        title: 'Are you sure?',
        text: `This "${item.version}" cordatus onvif discovery version will be delete.`,
        showCancelButton: true,
        confirmButtonText: 'Yes, do it!'
      }).then(async (result) => {
        if (result.isConfirmed) {
          await apiService
            .delete(`admin/onvif_discovery_version/${item.id}`)
            .then(() => {
              Swal.fire({
                icon: 'success',
                title: 'Deleted',
                text: 'Cordatus onvif discovery version deleted successfully!'
              })
              this.getOnvifDiscoveryVersionList()
            })
            .catch((err) => {
              Swal.fire({
                icon: 'error',
                title: 'Error while deleted cordatus onvif discovery version.',
                text: get(err, 'response.data.message', 'Something went wrong')
              })
            })
        }
      })
    }
  }
})
</script>

  <style>
.slow-ani {
  -webkit-animation: rotation 6s infinite linear;
  animation: rotation 6s infinite linear;
}

@keyframes rotation {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(359deg);
  }
}
</style>
